import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Scalar helpers
 */
const UUIDSchema = z.string().uuid();

/**
 * Date-only format validation (YYYY-MM-DD)
 */
const DateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format");

/**
 * Common attachment schema with URL validation
 */
const AttachmentUrlSchema = z.string().url().nullable().optional();

/**
 * Discount type enum matching backend model
 */
export const DiscountTypeSchema = z.enum(["PERCENT", "FIXED_PRICE"]);

/**
 * Discount target type enum matching backend model
 */
export const DiscountTargetTypeSchema = z.enum([
  "SKU",
  "PRODUCT_DISCOUNT_GROUP",
  "ALL_PRODUCTS",
  "PRODUCT_CATEGORY",
  "PRODUCT_LINE",
]);

/**
 * Pagination schema
 */
export const DiscountPaginationSchema = z.object({
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});

/**
 * Filters accepted by the list endpoint
 */
export const ListDiscountsInputSchema = z.object({
  search: z.string().optional(),
  discountType: z.array(DiscountTypeSchema).optional(),
  isActive: z.boolean().optional(),
  effectiveFrom: z.string().optional(),
  effectiveTo: z.string().optional(),
  customerId: UUIDSchema.optional(),
  limit: z.number().int().min(1).max(5000).optional(),
  offset: z.number().int().min(0).optional(),
});

/**
 * Customer summary for scope display
 */
export const DiscountScopeCustomerSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

/**
 * Product summary for scope display
 */
export const DiscountScopeProductSchema = z.object({
  id: UUIDSchema,
  name: z.string().nullable(),
  sku: z.string().nullable(),
  tags: z.array(z.string()).optional(),
});

/**
 * Product discount group summary
 */
export const DiscountScopeProductGroupSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

/**
 * Discount scope schema
 */
export const DiscountScopeSchema = z.object({
  id: UUIDSchema,
  discountId: UUIDSchema,
  customerId: UUIDSchema.nullable(),
  targetType: DiscountTargetTypeSchema,
  targetId: UUIDSchema.nullable(),
  targetValue: z.string().nullable().optional(),
  isActive: z.boolean(),
  attachment: z.string().url().nullable(),
  /**
   * End customer name - when set, this discount requires verification.
   * Represents the customer's customer (e.g., "Nestle" when our customer resells to Nestle).
   */
  endCustomerName: z.string().nullable().optional(),
  customer: DiscountScopeCustomerSchema.nullable().optional(),
  product: DiscountScopeProductSchema.nullable().optional(),
  productDiscountGroup: DiscountScopeProductGroupSchema.nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * User summary for created/modified by
 */
export const DiscountUserSchema = z.object({
  id: UUIDSchema,
  name: z.string().nullable(),
  email: z.string(),
});

/**
 * Main discount schema
 */
export const DiscountSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  name: z.string(),
  discountType: DiscountTypeSchema,
  percentBps: z.number().int().nullable(),
  value: z.union([z.number(), z.string()]).nullable(),
  effectiveStart: z.string().nullable(),
  effectiveEnd: z.string().nullable(),
  isActive: z.boolean(),
  createdByUserId: UUIDSchema.nullable(),
  lastModifiedByUserId: UUIDSchema.nullable(),
  createdBy: DiscountUserSchema.nullable().optional(),
  lastModifiedBy: DiscountUserSchema.nullable().optional(),
  scopes: z.array(DiscountScopeSchema).optional(),
  scopeCount: z.number().int().nonnegative().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * List response schema
 */
export const ListDiscountsResponseSchema = z.object({
  discounts: z.array(DiscountSchema),
  pagination: DiscountPaginationSchema,
});

/**
 * Create discount scope input schema - uses discriminated union to enforce
 * correct fields for each target type:
 * - SKU/PRODUCT_DISCOUNT_GROUP require targetId (UUID)
 * - PRODUCT_CATEGORY/PRODUCT_LINE require targetValue (string)
 * - ALL_PRODUCTS requires neither
 *
 * customerId is optional for all types - null means "all customers"
 */
export const CreateDiscountScopeInputSchema = z.discriminatedUnion(
  "targetType",
  [
    z.object({
      targetType: z.literal("SKU"),
      customerId: UUIDSchema.nullable().optional(), // null = all customers
      targetId: UUIDSchema, // Required for SKU
      attachment: AttachmentUrlSchema,
      endCustomerName: z.string().nullable().optional(), // End customer requiring verification
    }),
    z.object({
      targetType: z.literal("PRODUCT_DISCOUNT_GROUP"),
      customerId: UUIDSchema.nullable().optional(), // null = all customers
      targetId: UUIDSchema, // Required for product discount group
      attachment: AttachmentUrlSchema,
      endCustomerName: z.string().nullable().optional(), // End customer requiring verification
    }),
    z.object({
      targetType: z.literal("ALL_PRODUCTS"),
      customerId: UUIDSchema.nullable().optional(), // null = all customers
      targetId: UUIDSchema.nullable().optional(), // Optional for all products
      attachment: AttachmentUrlSchema,
      endCustomerName: z.string().nullable().optional(), // End customer requiring verification
    }),
    z.object({
      targetType: z.literal("PRODUCT_CATEGORY"),
      customerId: UUIDSchema.nullable().optional(), // null = all customers
      targetValue: z.string().min(1, "Category name is required"),
      attachment: AttachmentUrlSchema,
      endCustomerName: z.string().nullable().optional(), // End customer requiring verification
    }),
    z.object({
      targetType: z.literal("PRODUCT_LINE"),
      customerId: UUIDSchema.nullable().optional(), // null = all customers
      targetValue: z.string().min(1, "Product line name is required"),
      attachment: AttachmentUrlSchema,
      endCustomerName: z.string().nullable().optional(), // End customer requiring verification
    }),
  ],
);

/**
 * Create discount input schema with comprehensive validation:
 * - Percent values must be in range [0, 100]
 * - Dates must be in YYYY-MM-DD format
 * - effectiveEnd must be >= effectiveStart when both are present
 */
export const CreateDiscountInputSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    discountType: DiscountTypeSchema,
    value: z.number().positive("Value must be positive"),
    effectiveStart: DateOnlySchema.nullable().optional(),
    effectiveEnd: DateOnlySchema.nullable().optional(),
    scopes: z
      .array(CreateDiscountScopeInputSchema)
      .min(1, "At least one scope is required"),
  })
  .superRefine((data, ctx) => {
    // Validate percent value is <= 100 for PERCENT discount type
    if (data.discountType === "PERCENT" && data.value > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Percent discount must be between 0 and 100",
        path: ["value"],
      });
    }

    // Validate date order: effectiveEnd >= effectiveStart
    if (data.effectiveStart && data.effectiveEnd) {
      const start = new Date(data.effectiveStart);
      const end = new Date(data.effectiveEnd);
      if (end < start) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End date must be on or after start date",
          path: ["effectiveEnd"],
        });
      }
    }
  });

/**
 * Create discount response schema
 */
export const CreateDiscountResponseSchema = z.object({
  discount: DiscountSchema,
});

/**
 * List price result for a single SKU
 */
export const ListPriceResultSchema = z.object({
  sku: z.string(),
  listPrice: z.number(),
  currencyCode: z.string().nullable(),
  priceListName: z.string().nullable(),
});

/**
 * Get list prices input schema
 */
export const GetListPricesInputSchema = z.object({
  customerId: UUIDSchema,
  skus: z.array(z.string()).min(1).max(500),
});

/**
 * Get list prices response schema
 */
export const GetListPricesResponseSchema = z.object({
  prices: z.record(z.string(), ListPriceResultSchema.nullable()),
});

/**
 * Get available categories response schema
 * Returns distinct PRODVIEW categories and product lines for autocomplete
 */
export const GetAvailableCategoriesResponseSchema = z.object({
  categories: z.array(z.string()),
  productLines: z.array(z.string()),
});

/**
 * Product discount group schema for list endpoint
 */
export const ProductDiscountGroupSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  description: z.string().nullable(),
  itemCount: z.number().int().nonnegative(),
});

/**
 * Get product discount groups response schema
 */
export const GetProductDiscountGroupsResponseSchema = z.object({
  groups: z.array(ProductDiscountGroupSchema),
});

/**
 * Delete discount input schema
 */
export const DeleteDiscountInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Delete discount response schema
 */
export const DeleteDiscountResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * Discounts contracts
 */
export const discountsContracts = {
  list: oc
    .input(ListDiscountsInputSchema)
    .output(ListDiscountsResponseSchema)
    .route({
      method: "GET",
    }),
  create: oc
    .input(CreateDiscountInputSchema)
    .output(CreateDiscountResponseSchema)
    .route({
      method: "POST",
    }),
  getListPrices: oc
    .input(GetListPricesInputSchema)
    .output(GetListPricesResponseSchema)
    .route({
      method: "POST", // POST because we're sending a list of SKUs in the body
    }),
  getAvailableCategories: oc
    .output(GetAvailableCategoriesResponseSchema)
    .route({
      method: "GET",
      path: "/categories",
    }),
  getProductDiscountGroups: oc
    .output(GetProductDiscountGroupsResponseSchema)
    .route({
      method: "GET",
      path: "/product-discount-groups",
    }),
  delete: oc
    .input(DeleteDiscountInputSchema)
    .output(DeleteDiscountResponseSchema)
    .route({
      method: "DELETE",
    }),
} as const;

// Type exports
export type DiscountType = z.infer<typeof DiscountTypeSchema>;
export type DiscountTargetType = z.infer<typeof DiscountTargetTypeSchema>;
export type Discount = z.infer<typeof DiscountSchema>;
export type DiscountScope = z.infer<typeof DiscountScopeSchema>;
export type DiscountUser = z.infer<typeof DiscountUserSchema>;
export type ListDiscountsInput = z.infer<typeof ListDiscountsInputSchema>;
export type ListDiscountsResponse = z.infer<typeof ListDiscountsResponseSchema>;
export type CreateDiscountScopeInput = z.infer<
  typeof CreateDiscountScopeInputSchema
>;
export type CreateDiscountInput = z.infer<typeof CreateDiscountInputSchema>;
export type CreateDiscountResponse = z.infer<
  typeof CreateDiscountResponseSchema
>;
export type ListPriceResult = z.infer<typeof ListPriceResultSchema>;
export type GetListPricesInput = z.infer<typeof GetListPricesInputSchema>;
export type GetListPricesResponse = z.infer<typeof GetListPricesResponseSchema>;
export type GetAvailableCategoriesResponse = z.infer<
  typeof GetAvailableCategoriesResponseSchema
>;
export type ProductDiscountGroup = z.infer<typeof ProductDiscountGroupSchema>;
export type GetProductDiscountGroupsResponse = z.infer<
  typeof GetProductDiscountGroupsResponseSchema
>;
export type DeleteDiscountInput = z.infer<typeof DeleteDiscountInputSchema>;
export type DeleteDiscountResponse = z.infer<
  typeof DeleteDiscountResponseSchema
>;
